import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    MobileIcon,
    ModalHeader,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import { toast } from "react-toastify";
import { Save_User } from "../../Redux/actions";

const editProfileSchema = Yup.object().shape({
    name: Yup.string()
        .matches(/^[A-Za-z0-9. ]+$/, "Only letters, numbers, spaces & dots are allowed.")
        .min(4, "Name must be at least 4 characters long")
        .required("Name is required"),
    mobile: Yup.string()
        .matches(/^\d{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required"),
    email: Yup.string()
        .email("Invalid email format")
        .nullable(),
    businessName: Yup.string()
        .nullable(),
    gstNo: Yup.string()
        .matches(/^[0-9A-Z]{15}$/, "GST number must be 15 characters")
        .nullable(),
    panNumber: Yup.string()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN number format")
        .nullable(),
    // city: Yup.string()
    //     .nullable(),
    // businessEmail: Yup.string()
    //     .email("Invalid email format")
    //     .nullable(),
    businessAddress: Yup.string()
        .nullable(),
});

const EditProfile = ({ handleClose }) => {
    const dispatch = useDispatch();
    const [height, setHeight] = useState(window.innerHeight);
    const user = useSelector((state) => state.UserReducer.user);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const initialValues = {
        name: user?.name || "",
        mobile: user?.mobile || "",
        email: user?.email || "",
        businessName: user?.businessName || "",
        gstNo: user?.gstNo || "",
        panNumber: user?.panNumber || "",
        city: user?.city || "",
        businessEmail: user?.businessEmail || "",
        businessAddress: user?.businessAddress || "",
    };

    const editProfileFunction = async (values) => {
        const payload = {
            name: values.name,
            mobile: values.mobile,
            email: values.email || null,
            businessName: values.businessName || null,
            gstNo: values.gstNo || null,
            panNumber: values.panNumber || null,
            // city: values.city || null,
            // businessEmail: values.businessEmail || null,
            businessAddress: values.businessAddress || null,
        };

        const response = await axios.post(
            `${Config.apiUrl}/client/edit/profile`,
            payload,
            {
                headers: {
                    authorization: "Bearer " + user.token,
                    "Content-Type": "application/json",
                },
            }
        );

        return { data: response.data, formValues: values };
    };

    const onSuccess = (result) => {
        const { data, formValues } = result;
        if (data.isSuccess) {
            toast.success(data.msg || "Profile updated successfully");
            const updatedUser = {
                ...user,
                name: formValues.name,
                mobile: formValues.mobile,
                email: formValues.email,
                businessName: formValues.businessName,
                gstNo: formValues.gstNo,
                panNumber: formValues.panNumber,
                businessAddress: formValues.businessAddress,
            };
            dispatch(Save_User(updatedUser));
            handleClose();
        } else {
            toast.error(data.msg || "Failed to update profile");
        }
    };

    const onError = (error) => {
        toast.error(error?.response?.data?.msg || "Failed to update profile");
    };

    const { mutate: editProfile, isLoading } = useMutation(editProfileFunction, {
        onSuccess: onSuccess,
        onError: onError,
    });

    const handleSubmit = (values) => {
        editProfile(values);
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Edit Profile</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={editProfileSchema}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <>
                            <Form>
                                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                    <Wrapper>
                                        {/* Name - Mandatory */}
                                        <InputGroup>
                                            <Label htmlFor="name">Name *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="name"
                                                    id="name"
                                                    autoComplete="off"
                                                />
                                            </FieldWrappers>
                                            {errors.name && touched.name && (
                                                <ErrorMessage>{errors.name}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* Mobile - Mandatory & Disabled */}
                                        <InputGroup>
                                            <Label htmlFor="mobile">Mobile Number *</Label>
                                            <FieldWrappers className="bg-gray-100">
                                                <MobileIcon />
                                                <Field
                                                    type="text"
                                                    name="mobile"
                                                    id="mobile"
                                                    autoComplete="off"
                                                    disabled={true}
                                                    className="w-full pl-5 cursor-not-allowed"
                                                />
                                            </FieldWrappers>
                                            {errors.mobile && touched.mobile && (
                                                <ErrorMessage>{errors.mobile}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* Email */}
                                        <InputGroup>
                                            <Label htmlFor="email">Email</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="email"
                                                    name="email"
                                                    id="email"
                                                    autoComplete="off"
                                                />
                                            </FieldWrappers>
                                            {errors.email && touched.email && (
                                                <ErrorMessage>{errors.email}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* Business Name */}
                                        <InputGroup>
                                            <Label htmlFor="businessName">Business Name</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="businessName"
                                                    id="businessName"
                                                    autoComplete="off"
                                                />
                                            </FieldWrappers>
                                            {errors.businessName && touched.businessName && (
                                                <ErrorMessage>{errors.businessName}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* GST Number */}
                                        <InputGroup>
                                            <Label htmlFor="gstNo">GST Number</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="gstNo"
                                                    id="gstNo"
                                                    autoComplete="off"
                                                    maxLength={15}
                                                    onChange={(e) => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFieldValue("gstNo", value);
                                                    }}
                                                />
                                            </FieldWrappers>
                                            {errors.gstNo && touched.gstNo && (
                                                <ErrorMessage>{errors.gstNo}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* PAN Number */}
                                        <InputGroup>
                                            <Label htmlFor="panNumber">PAN Number</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="panNumber"
                                                    id="panNumber"
                                                    autoComplete="off"
                                                    maxLength={10}
                                                    onChange={(e) => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFieldValue("panNumber", value);
                                                    }}
                                                />
                                            </FieldWrappers>
                                            {errors.panNumber && touched.panNumber && (
                                                <ErrorMessage>{errors.panNumber}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* City */}
                                        {/* <InputGroup>
                                            <Label htmlFor="city">City</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="city"
                                                    id="city"
                                                    autoComplete="off"
                                                />
                                            </FieldWrappers>
                                            {errors.city && touched.city && (
                                                <ErrorMessage>{errors.city}</ErrorMessage>
                                            )}
                                        </InputGroup> */}

                                        {/* Business Email */}
                                        {/* <InputGroup>
                                            <Label htmlFor="businessEmail">Business Email</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="email"
                                                    name="businessEmail"
                                                    id="businessEmail"
                                                    autoComplete="off"
                                                />
                                            </FieldWrappers>
                                            {errors.businessEmail && touched.businessEmail && (
                                                <ErrorMessage>{errors.businessEmail}</ErrorMessage>
                                            )}
                                        </InputGroup> */}

                                        {/* Business Address */}
                                        {/* <InputGroup>
                                            <Label htmlFor="businessAddress">Business Address</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="businessAddress"
                                                    id="businessAddress"
                                                    autoComplete="off"
                                                />
                                            </FieldWrappers> */}
                                        <InputGroup>
                                            <Label htmlFor="businessAddress">Business Address</Label>
                                            <FieldWrapper>
                                                <Field
                                                    as={Textarea}
                                                    id="businessAddress"
                                                    name="businessAddress"
                                                    rows={4}
                                                    maxLength={500}
                                                    placeholder="Write your address here..."
                                                />
                                            </FieldWrapper>

                                            {errors.businessAddress && touched.businessAddress && (
                                                <ErrorMessage>{errors.businessAddress}</ErrorMessage>
                                            )}
                                        </InputGroup>
                                    </Wrapper>
                                </ScrollableContent>
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={handleClose}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={isLoading}>
                                        {isLoading ? "Updating..." : "Update"}
                                    </SubmitBtn>
                                </ActionSection>
                            </Form>
                        </>
                    )}
                </Formik>
            </div>
        </Container >
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;
const FieldWrapper = tw.div`w-full`;
const Textarea = tw.textarea`
    min-h-30 border border-gray-300 
    rounded pl-2 py-2 
    focus:ring-1 focus:ring-sky-600 
    focus:border-sky-600 focus:outline-none 
    duration-200 w-full h-30 
    resize-y break-words overflow-hidden
`;
export default EditProfile;